import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Check, Clock3 } from "lucide-react";
import RelatedContentCarousel from "@/components/RelatedContentCarousel";

export const INTEGRATIONS_DEFAULT_DATA = {
  breadcrumb_label: "All Products",
  breadcrumb_href: "/products",
  hero_title: "Integrations",
  hero_tagline: "Connect Antera with the tools you already use. Our integrations ecosystem ensures data flows smoothly between all your systems, eliminating manual data entry and reducing errors.",
  hero_cta_label: "Request a Demo",
  hero_cta_href: "/contact",
  hero_image: "/api/media/acd5f68d-154c-447a-9ae5-087a270de539",
  hub_graphic: {
    src: "https://customer-assets.emergentagent.com/job_antera-next/artifacts/5tyb0v6i_image-10.png",
    alt: "Antera at the Center of Your Business - one connected platform with real-time data flow across suppliers, accounting (QuickBooks, NetSuite, Xero, Sage, Microsoft Dynamics), eCommerce (Aether Webstores, Shopify, BigCommerce, WooCommerce, Magento), shipping and logistics (ShipStation, UPS, FedEx, USPS), payments (Fiserv CardConnect), tax and compliance (Avalara, TaxJar), and CRM and marketing (HubSpot, SimpleTexting)",
  },
  ecosystem_heading: "Integration Ecosystem",
  ecosystem_subheading: "Every integration below is live in production today.",
  ecosystem: [
    { title: "Accounting", items: [
      { name: "QuickBooks Online", available: true }, { name: "Xero", available: true },
      { name: "Sage", available: true }, { name: "NetSuite", available: true },
      { name: "Microsoft Dynamics", available: true }, { name: "Sage Accounting", available: true },
    ]},
    { title: "PromoStandards", items: [
      { name: "Product Data", available: true }, { name: "Inventory", available: true },
      { name: "Order Status", available: true }, { name: "Invoicing", available: true },
      { name: "Purchase Orders", available: true }, { name: "Shipping", available: true },
    ]},
    { title: "Payments", items: [
      { name: "Fiserv CardConnect", available: true, children: [
        { name: "Integrated Payments", available: true }, { name: "Credit Card Processing", available: true },
        { name: "eCheck / ACH", available: true }, { name: "Fully PCI Compliant", available: true },
      ]},
    ]},
    { title: "Tax & Compliance", items: [
      { name: "Avalara", available: true, children: [
        { name: "AvaTax", available: true }, { name: "Real-Time Tax Calculation", available: true },
        { name: "Automated Filing", available: true }, { name: "Multi-State Compliance", available: true },
      ]},
      { name: "TaxJar", available: true }, { name: "Antera Tax", available: true },
    ]},
    { title: "eCommerce", items: [
      { name: "Aether Webstores", available: true, children: [
        { name: "Corporate Program Stores", available: true }, { name: "PunchOut catalog Ready", available: true },
        { name: "Fully integrated with Advance", available: true },
      ]},
      { name: "Shopify", available: true }, { name: "Liftoff", available: true },
      { name: "BigCommerce", available: true }, { name: "Bright Sites", available: true },
      { name: "Cart.com", available: true }, { name: "WooCommerce", available: true },
      { name: "Magento", available: true },
    ]},
    { title: "Shipping", items: [
      { name: "ShipStation", available: true, children: [
        { name: "Real-Time Rate Shopping", available: true }, { name: "Tracking Automation", available: true },
        { name: "UPS, FedEx, USPS, 3PLs, and more", available: true },
      ]},
      { name: "Ship Hero", available: true },
    ]},
  ],
  utilities_heading: "Utilities",
  utilities_subheading: "Productivity layers integrated across the platform.",
  utilities: [
    { name: "Simple Texting", available: true }, { name: "CXML", available: true },
    { name: "HubSpot", available: true }, { name: "AI Integrated", available: true },
  ],
  bottom_cta: {
    heading: "Need a custom integration?",
    subheading: "Our team can help connect Antera with your existing systems through our API and webhook support.",
    button_label: "Contact Us",
    button_href: "/contact",
  },
  related: {
    tag: "Integrated Partner",
    title: "Latest Integration News",
    subtitle: "Partner spotlights and integration updates from the Antera ecosystem.",
  },
};

function flattenItems(items, indent = 0, out = []) {
  for (const it of items) {
    out.push({ ...it, indent });
    if (it.children && it.children.length) flattenItems(it.children, indent + 1, out);
  }
  return out;
}

function IntegrationItem({ item }) {
  const indent = item.indent || 0;
  const Icon = item.available ? Check : Clock3;
  const iconClass = item.available ? "text-emerald-500" : "text-slate-300";
  const textClass = item.available
    ? indent === 0 ? "text-sm font-semibold text-slate-800" : "text-sm text-slate-700"
    : "text-sm text-slate-400 line-through decoration-slate-200";
  const padLeft = indent === 0 ? "" : "ml-6 pl-3 border-l-2 border-slate-100";
  return (
    <li className={`flex items-center gap-2 ${padLeft}`} data-testid={`integration-row-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className={`h-4 w-4 shrink-0 rounded-full flex items-center justify-center ${item.available ? "bg-emerald-50" : "bg-slate-100"}`} aria-hidden>
        <Icon className={`h-3 w-3 ${iconClass}`} />
      </span>
      <span className={textClass}>{item.name}</span>
      {!item.available && indent === 0 && (
        <span className="ml-1.5 text-[10px] font-semibold text-slate-400 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 uppercase tracking-wider">Coming</span>
      )}
    </li>
  );
}

function EcosystemCard({ section, i }) {
  const flat = flattenItems(section.items);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.4 }}
      data-testid={`ecosystem-card-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
      <h3 className="text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">{section.title}</h3>
      <ul className="space-y-2.5">
        {flat.map((item, idx) => <IntegrationItem key={`${item.name}-${idx}`} item={item} />)}
      </ul>
    </motion.div>
  );
}

export default function IntegrationsPage({ data }) {
  const d = { ...INTEGRATIONS_DEFAULT_DATA, ...(data || {}) };
  const bottom = { ...INTEGRATIONS_DEFAULT_DATA.bottom_cta, ...(d.bottom_cta || {}) };
  const related = { ...INTEGRATIONS_DEFAULT_DATA.related, ...(d.related || {}) };
  const hub = { ...INTEGRATIONS_DEFAULT_DATA.hub_graphic, ...(d.hub_graphic || {}) };

  return (
    <div data-testid="integrations-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {d.breadcrumb_label ? (
              <Link to={d.breadcrumb_href || "/products"} className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 mb-6">
                <ArrowLeft className="h-3.5 w-3.5" /> {d.breadcrumb_label}
              </Link>
            ) : null}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">{d.hero_title}</h1>
                <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed">{d.hero_tagline}</p>
                <Link to={d.hero_cta_href || "/contact"} className="inline-block mt-8">
                  <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-12 text-sm font-semibold">
                    {d.hero_cta_label} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              {d.hero_image ? (
                <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200">
                  <img src={d.hero_image} alt="Integrations" className="w-full h-64 lg:h-80 object-cover" />
                </div>
              ) : null}
            </div>
          </motion.div>
        </div>
      </section>

      {hub.src ? (
        <section data-testid="integrations-hub-graphic-section" className="py-8 lg:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-lg">
              <img src={hub.src} alt={hub.alt} data-testid="integrations-hub-graphic" className="w-full h-auto" loading="lazy" />
            </div>
          </div>
        </section>
      ) : null}

      <section className="py-16 lg:py-24 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{d.ecosystem_heading}</h2>
            <p className="mt-3 text-sm lg:text-base text-slate-500">{d.ecosystem_subheading}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(d.ecosystem || []).map((section, i) => <EcosystemCard key={section.title} section={section} i={i} />)}
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25, duration: 0.4 }}
            data-testid="ecosystem-card-utilities"
            className="mt-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border border-slate-700 rounded-xl p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="lg:max-w-xs">
                <h3 className="text-lg font-bold">{d.utilities_heading}</h3>
                <p className="text-xs text-slate-400 mt-1">{d.utilities_subheading}</p>
              </div>
              <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 flex-1">
                {(d.utilities || []).map((u) => (
                  <li key={u.name} data-testid={`utilities-row-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-emerald-400" />
                    </span>
                    <span className="text-sm font-medium text-slate-100">{u.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{bottom.heading}</h2>
          <p className="mt-3 text-sm text-sky-100">{bottom.subheading}</p>
          <Link to={bottom.button_href || "/contact"} className="inline-block mt-6">
            <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
              {bottom.button_label} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedContentCarousel tag={related.tag} title={related.title} subtitle={related.subtitle} testId="integrations-related" />
    </div>
  );
}
